import { useMemo } from 'react';

import { useGetEarningById } from '~/react-query-hooks/earnings/useGetEarningById';
import { useEarningsInsightsFilter } from '~/react-query-hooks/insights/useEarningsInsightsFilter';
import { deduplicateEarningsInsights } from '~/react-query-hooks/insights/useEarningsInsightsFilter.utils';
import { useSubscription } from '~/stores/use-subscription/useSubscription';

/**
 * Fetches and returns the most relevant earnings insight for a given entity.
 *
 * Deduplicates insights by earnings_id (preferring recap over preview per event),
 * then selects the most recent by created_at. This ensures newer previews (e.g. Q4)
 * are shown over older recaps (e.g. Q3) when both exist.
 *
 *  */
export const useEarningsInsightsByEntity = (entityTag: string) => {
  const isBasicOrLightUser = useSubscription(state =>
    state.isBasicOrLightUser()
  );

  const { data: earningsInsightsData = [], isFetching: isInsightsFetching } =
    useEarningsInsightsFilter({
      body: { entities: [entityTag] },
      isEnabled: !isBasicOrLightUser,
    });

  const latestEarningsInsight = useMemo(() => {
    const deduplicated = deduplicateEarningsInsights(earningsInsightsData);
    if (deduplicated.length === 0) return undefined;
    const sorted = [...deduplicated].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
    return sorted[0];
  }, [earningsInsightsData]);

  const { data: earningsData, isFetching: isEarningsFetching } =
    useGetEarningById(latestEarningsInsight?.earnings_id);

  const isLoading = isInsightsFetching || isEarningsFetching;

  return {
    isLoading,
    earningsData,
    earningsInsight: latestEarningsInsight,
  };
};
